import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import { Drawer, Button, Input, Space, Row, Col, InputNumber, Spin, Popconfirm, Table, DatePicker, Alert, Tooltip, Select, message, Modal } from 'antd';
import {
    BankOutlined,
    CreditCardOutlined,
    DeleteOutlined,
    PaperClipOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    UserOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import { _newLine, paymentMethodOptions, isValidDateForm, serializeDateTime, renderMoney } from '@/utils/helpers';
import { createReceipt, getCustomerDepositSummary, previewReceiptMutation, updateReceipt } from '@/redux/actions/receiptActions';
import { fetchCustomer } from '@/redux/actions/customerActions';
import { fetchProduct } from '@/redux/actions/productActions';
import moment from 'moment';
import CustomerSelect from '@/components/customers/CustomerSelect';
import ProductSelect from '@/components/products/ProductSelect';
import ImageSelector from '@/components/ImageSelector';
import { checkPermission } from '@/utils/permission';
import { getReceiptDepositLimit, getSelectedDepositBalance } from '@/components/customers/customerDepositAllocation';
import {
    appendCustomerServices,
    buildReceiptMutationControlFields,
    buildReceiptMutationConfirmationSummary,
    getAvailableCustomerServices,
    getReceiptItemValidationMessage,
} from './receiptMutationPresentation';
import './ReceiptForm.less';

const fieldLabel = (label, guide) => <Tooltip title={guide}><span>{label} <QuestionCircleOutlined style={{ color: '#8c8c8c' }} /></span></Tooltip>;

const ReceiptSection = ({ icon, title, description, extra, financial, children }) => (
    <section className={`receipt-form-section${financial ? ' receipt-form-section--financial' : ''} mb-3`}>
        <div className="receipt-form-section__header">
            <div className="receipt-form-section__heading">
                <span className="receipt-form-section__icon">{icon}</span>
                <div>
                    <div className="receipt-form-section__title">{title}</div>
                    {description && <div className="receipt-form-section__description">{description}</div>}
                </div>
            </div>
            {extra && <div className="receipt-form-section__extra">{extra}</div>}
        </div>
        <div className="receipt-form-section__body">{children}</div>
    </section>
);

const PaymentSourceBox = ({ icon, title, available, tone, children, target, remaining }) => (
    <section className={`receipt-payment-source receipt-payment-source--${tone}`}>
        <div className="receipt-payment-source__header">
            <div className="receipt-payment-source__title">
                <span className="receipt-payment-source__icon">{icon}</span>
                <span>{title}</span>
            </div>
            <div className="receipt-payment-source__available">
                <span>Có</span>
                <strong>{renderMoney(available)}</strong>
            </div>
        </div>

        <div className="receipt-payment-source__body">{children}</div>

        <div className="receipt-payment-source__summary">
            <div>
                <span>Áp dụng cho:</span>
                <strong>{target}</strong>
            </div>
            <div>
                <span>Còn lại:</span>
                <strong>{renderMoney(remaining)}</strong>
            </div>
        </div>
    </section>
);

class ReceiptForm extends Component {
    constructor() {
        super();
        this.state = {
            isLoading: false,
            submitting: false,
            items: [],
            images: [],
            net_total: 0,
            payment_breakdowns: [{ key: `payment-${Date.now()}`, payment_method: null, amount: 0, provider_name: 'VNĐ' }],
            customer_debt: 0,
            customer_balance: 0,
            deposit_data: {},
            available_customer_services: [],
            selected_customer_service_ids: [],
            service_options_loading: false,
            special_discount: 0,
            receiptRequestKey: `receipt-${Date.now()}-${Math.random().toString(36).slice(2)}`
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const isOpening = nextProps.visible && !this.props.visible;
        const isChangingReceipt = nextProps.visible
            && nextProps.currentReceipt !== this.props.currentReceipt;

        if (isOpening || isChangingReceipt) {
            this.initializeFormData(nextProps.currentReceipt);
        }
    }

    initializeFormData = (currentReceipt) => {
        this.props.form.resetFields();

        if (!currentReceipt) {
            return;
        }

        const items = Array.isArray(currentReceipt.items)
            ? currentReceipt.items.map(item => ({
                key: `${item.type}-${item.id}`,
                id: item.id,
                receipt_item_id: item.receipt_id ? item.id : item.receipt_item_id,
                origin_id: item.origin_id,
                product_sale_id: item.product_sale_id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                line_total: Number(item.price || 0) * Number(item.quantity || 0),
                amount: item.type === 'Sản phẩm' ? item.quantity * item.price : item.amount,
                amount_to_pay: item.amount_to_pay,
                outstanding_amount: item.outstanding_amount,
                allocated_amount: item.allocated_amount,
                type: item.type,
                customer_service_id: item.customer_service_id,
            })).filter((item, index, allItems) => allItems.findIndex(other => other.key === item.key) === index)
            : [];
        const breakdowns = (currentReceipt.payment_breakdowns || currentReceipt.paymentBreakdowns || [])
            .map(row => ({ ...row, key: row.id || `payment-${Date.now()}-${Math.random()}` }));

        // Mỗi lần mở form edit đều bind lại từ phiếu hiện tại vì state đã được dọn khi đóng form.
        this.setState({
            items,
            net_total: Number(currentReceipt.amount_paid || 0),
            special_discount: Number(currentReceipt.special_discount || 0),
            payment_breakdowns: breakdowns.length ? breakdowns : [{
                key: `payment-${Date.now()}`,
                payment_method: currentReceipt.payment_method,
                amount: Number(currentReceipt.amount_paid || 0),
                provider_name: 'VNĐ',
            }],
        }, () => this.loadAvailableCustomerServices(currentReceipt));
    };

    confirmFinancialMutation = preview => new Promise((resolve, reject) => {
        const summary = buildReceiptMutationConfirmationSummary(preview);
        Modal.confirm({
            title: 'Xác nhận phân bổ lại dữ liệu tài chính',
            width: isMobile ? '100%' : '55%',
            maskClosable: false,
            content: (
                <div>
                    <p>Thay đổi này ảnh hưởng {summary.changedCount} dòng phiếu thu.</p>
                    <p>Hệ thống sẽ đảo {renderMoney(summary.productReversal)} thanh toán sản phẩm và {renderMoney(summary.serviceReversal)} phân bổ dịch vụ, sau đó đối soát lại theo nội dung mới.</p>
                    {/* {summary.warnings.map((warning, index) => <Alert key={index} type="warning" message={warning} className="mb-2" />)} */}
                </div>
            ),
            okText: 'Xác nhận và lưu',
            cancelText: 'Xem lại',
            onOk: () => resolve(true),
            onCancel: () => reject(new Error('CANCELLED')),
        });
    });

    async submit(e) {
        e.preventDefault();
        this.props.form.validateFields(async (err, values) => {
            if (!err) {
                const enteredPaymentRows = this.state.payment_breakdowns.filter(row => row.payment_method || Number(row.amount || 0) > 0);
                const invalidPayment = enteredPaymentRows.some(row => !row.payment_method || Number(row.amount || 0) <= 0);
                if (invalidPayment) {
                    message.error('Vui lòng chọn hình thức và nhập số tiền lớn hơn 0 cho từng dòng.', 5);
                    return;
                }
                const itemValidationMessage = getReceiptItemValidationMessage(this.state.items);
                if (itemValidationMessage) {
                    message.error(itemValidationMessage, 5);
                    return;
                }
                this.setState({ submitting: true, isLoading: true });
                try {
                    const { items, images } = this.state;

                    const amountPaid = this.state.payment_breakdowns
                        .filter(row => row.payment_method || Number(row.amount || 0) > 0)
                        .reduce((sum, row) => sum + Number(row.amount || 0), 0);
                    const serviceAmount = this.getServiceAmount(items);
                    const serviceCashAmount = Math.max(0, amountPaid - this.getProductAmount(items));
                    const serviceNeed = Math.max(0, serviceAmount - Number(values.special_discount || 0));
                    const requestedBalanceAmount = Math.max(0, Number(values.balance_amount || 0));
                    const requestedDepositAmount = Math.max(0, Number(values.deposit_amount || 0));
                    if (requestedBalanceAmount + requestedDepositAmount > Math.max(0, serviceNeed - serviceCashAmount)) {
                        message.error('Tổng tiền dịch vụ từ tiền thực nộp, số dư và tiền cọc đang vượt số cần thanh toán.', 5);
                        this.setState({ submitting: false, isLoading: false });
                        return;
                    }

                    values.items = items;
                    values.payment_at = serializeDateTime(values.payment_at, 'date');
                    values.payment_breakdowns = this.state.payment_breakdowns
                        .filter(row => row.payment_method || Number(row.amount || 0) > 0)
                        .map(({ key, ...row }) => row);
                    values.amount_paid = values.payment_breakdowns.reduce((sum, row) => sum + Number(row.amount || 0), 0);
                    values.net_total = values.amount_paid;
                    delete values.payment_method;
                    const depositAmount = Math.max(0, Number(values.deposit_amount) || 0);
                    values.deposit_application = depositAmount > 0 ? {
                        deposit_ids: values.deposit_ids,
                        amount: depositAmount,
                        reason: values.deposit_reason,
                    } : null;
                    const balanceAmount = Math.max(0, Number(values.balance_amount) || 0);
                    values.balance_customer_service_id = values.balance_customer_service_id || 'ALL'; // Mạc định là áp dụng cho tất cả dịch vụ nếu không chọn dịch vụ cụ thể.
                    const applyBalanceToAll = values.balance_customer_service_id === 'ALL';
                    values.balance_application = balanceAmount > 0 ? {
                        amount: balanceAmount,
                        customer_service_id: applyBalanceToAll ? null : values.balance_customer_service_id,
                        target: applyBalanceToAll ? 'ALL' : 'SERVICE',
                        reason: values.balance_reason,
                    } : null;
                    delete values.balance_amount;
                    delete values.balance_customer_service_id;
                    delete values.balance_reason;
                    delete values.deposit_ids;
                    delete values.deposit_amount;
                    delete values.deposit_reason;

                    let finalImages = images;
                    if (this.props.currentReceipt) {
                        const oldImages = Array.isArray(this.props.currentReceipt.images)
                            ? this.props.currentReceipt.images
                            : [];

                        // nếu images đang rỗng thì lấy oldImages
                        if (!finalImages || finalImages.length === 0) {
                            finalImages = oldImages.map((img) => ({
                                manual_id: img.manual_id ?? img.id ?? img.uid ?? null,
                                src: img.src ?? img.url ?? img.originalImage ?? img,
                            })).filter(x => x.src);
                        }
                    }

                    values.images = finalImages;

                    if (this.props.currentReceipt) {
                        values.customer_id = this.props.currentReceipt.customer_id;
                        values.commission_revision = this.props.currentReceipt.commission_revision || 1;
                        const preview = await this.props.previewReceiptMutation(this.props.currentReceipt.id, values);
                        if (preview.requires_confirmation) {
                            await this.confirmFinancialMutation(preview);
                        }
                        const mutationKey = `receipt-mutation-${this.props.currentReceipt.id}-${values.commission_revision}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                        Object.assign(values, buildReceiptMutationControlFields(preview, mutationKey));
                        await this.props.updateReceipt(this.props.currentReceipt.id, values).then((res) => {
                            return Promise.resolve(this.props.reloadData()).then(() => this.onClose());
                        }).catch(error => {
                            this.setState({ submitting: false, isLoading: false });
                        });

                    } else {
                        values.idempotency_key = this.state.receiptRequestKey;
                        await this.props.createReceipt(values).then((res) => {
                            this.setState({ receiptRequestKey: `receipt-${Date.now()}-${Math.random().toString(36).slice(2)}` });
                            this.props.reloadData();
                            this.onClose();
                        }).catch(error => {
                            this.setState({ submitting: false, isLoading: false });
                        });

                    }

                } catch (error) {
                    this.setState({ submitting: false, isLoading: false });
                }
            }
        });
    }

    onClose() {
        this.props.onCancel();
        this.props.form.resetFields();
        this.setState({
            isLoading: false,
            submitting: false,
            images: [],
            customer_debt: 0,
            customer_balance: 0,
            deposit_data: {},
            available_customer_services: [],
            selected_customer_service_ids: [],
            service_options_loading: false,
            items: [],
            net_total: 0,
            special_discount: 0,
            payment_breakdowns: [{ key: `payment-${Date.now()}`, payment_method: null, amount: 0, provider_name: 'VNĐ' }],
        });
    }

    renderNewItem(items = [], shouldRecalculate = true) {
        let new_items = items.map((item, index) => {
            return {
                id: item.id,
                key: item.key || `${item.type}-${item.id}`,
                receipt_item_id: item.receipt_id ? item.id : item.receipt_item_id,
                origin_id: item.origin_id,
                product_sale_id: item.product_sale_id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                line_total: Number(item.price || 0) * Number(item.quantity || 0),
                amount: item.type === 'Sản phẩm' ? item.quantity * item.price : item.amount,
                amount_to_pay: item.amount_to_pay,
                outstanding_amount: item.outstanding_amount,
                allocated_amount: item.allocated_amount,
                type: item.type,
                customer_service_id: item.customer_service_id
            }
        })
        this.setState({
            items: new_items.filter((v, i, a) => a.findIndex(v2 => v2.key === v.key) === i)
        }, () => {
            if (shouldRecalculate) {
                this.calculateTotalAmount(); // Gọi sau khi setState để đảm bảo rằng state đã được cập nhật
            }
        });
    }

    onDelete(key) {
        let items = this.state.items.filter(item => {
            return item.key !== key;
        });
        this.renderNewItem(items)
    }

    async onChangeItem(name, value, itemKey) {
        let items = [...this.state.items];
        let index = items.findIndex(item => item.key === itemKey);
        let currentItem = items[index];
        currentItem[name] = value;
        items[index] = currentItem;
        this.renderNewItem(items)
    }

    onChangeProduct(id) {
        this.setState({ isloading: true })
        this.props.fetchProduct(id).then((item) => {

            let new_items = {
                id: item.id,
                key: `Sản phẩm-${item.id}`,
                origin_id: item.id,
                name: item.name,
                price: item.sale_price,
                quantity: 1,
                amount: item.sale_price * 1,
                type: 'Sản phẩm'
            }
            this.setState({
                items: [...this.state.items, new_items].filter((v, i, a) => a.findIndex(v2 => v2.key === v.key) === i),
                isloading: false
            }, () => {
                this.calculateTotalAmount(); // Gọi sau khi setState để đảm bảo rằng state đã được cập nhật
            });
        }).catch(err => {
            this.setState({ isloading: false })
        })
        this.calculateTotalAmount();
    }

    loadAvailableCustomerServices = currentReceipt => {
        if (!currentReceipt?.customer_id) return;
        this.setState({ service_options_loading: true });
        this.props.fetchCustomer(currentReceipt.customer_id).then(customer => {
            this.setState({
                available_customer_services: customer?.active_purchased_services || [],
                service_options_loading: false,
            });
        }).catch(() => this.setState({ service_options_loading: false }));
    };

    addSelectedCustomerServices = () => {
        const nextItems = appendCustomerServices(
            this.state.items,
            this.state.available_customer_services,
            this.state.selected_customer_service_ids
        );
        this.setState({ selected_customer_service_ids: [] });
        this.renderNewItem(nextItems);
    };

    onChangeCustomer(id) {
        this.setState({ isLoading: true, deposit_data: {} });
        this.props.form.resetFields([
            'balance_amount',
            'balance_customer_service_id',
            'deposit_ids',
            'deposit_amount',
        ]);
        this.props.fetchCustomer(id).then((item) => {

            const receiptExportItems = item.receipt_export || [];
            const outstandingProductSales = item.outstanding_product_sales || [];
            const services = (item.active_purchased_services || []).filter(service => Number(service.outstanding_amount || 0) > 0);
            // Xử lý receiptExport
            const receiptExportMapped = receiptExportItems.map((item) => ({
                id: item.id,
                key: `Sản phẩm-${item.id}`,
                origin_id: item.product_id,
                name: item.name,
                type: 'Sản phẩm',
                price: item.sale_price,
                quantity: 1,
                amount: item.quantity * item.sale_price,
                line_total: item.quantity * item.sale_price,
            }));
            const outstandingProductMapped = outstandingProductSales.map((sale) => ({
                id: `product-sale-${sale.id}`,
                key: `Sản phẩm-còn-lại-${sale.id}`,
                product_sale_id: sale.id,
                origin_id: sale.product_id,
                name: sale.name,
                type: 'Sản phẩm',
                price: Number(sale.price || sale.unit_price || 0),
                quantity: Number(sale.quantity || 0),
                amount: Number(sale.amount || sale.sale_amount || 0),
                amount_to_pay: Number(sale.product_outstanding_amount || sale.outstanding_amount || 0),
                line_total: Number(sale.price || sale.unit_price || 0) * Number(sale.quantity || 0),
            }));

            // Xử lý services_selected
            const servicesMapped = services.map((service) => ({
                id: service.id,
                key: `Dịch vụ-${service.id}`,
                origin_id: service.service_id,
                name: service.service?.name ?? null,
                type: 'Dịch vụ',
                price: service.sale_price,
                quantity: service.quantity,
                amount: service.outstanding_amount,
                outstanding_amount: service.outstanding_amount,
                allocated_amount: service.allocated_amount,
                line_total: Number(service.sale_price || 0) * Number(service.quantity || 0),
                customer_service_id: service.id
            }));

            // Gộp hai mảng
            const mergedItems = [...outstandingProductMapped, ...receiptExportMapped, ...servicesMapped];
            // Render các phần tử mới
            this.renderNewItem(mergedItems);
            // Phiếu tạo mới mặc định theo cơ sở hiện tại của khách hàng vừa chọn.
            this.props.form.setFieldsValue({ branch_id: item.branch_id || null });
            this.setState({ isLoading: false, customer_debt: item.debt, customer_balance: Number(item.unallocated_balance || 0) }, () => {
                this.calculateNetTotal(); // Tính toán lại net_total
            });
            if (checkPermission('customer_deposit', 'apply')) {
                this.props.getCustomerDepositSummary(id).then(depositData => {
                    const nextDepositData = depositData || {};
                    const availableDeposits = (nextDepositData.active_deposits || [])
                        .filter(deposit => Number(deposit.current_balance || 0) > 0);

                    this.setState({ deposit_data: nextDepositData }, () => {
                        this.props.form.setFieldsValue({
                            deposit_ids: availableDeposits.length === 1
                                ? [Number(availableDeposits[0].id)]
                                : [],
                            deposit_amount: 0,
                        });
                    });
                }).catch(() => this.setState({ deposit_data: {} }));
            }
        }).catch(err => {
            this.setState({ items: [] })
        })

        this.calculateTotalAmount();
    }

    calculateTotalAmount = () => {
        let total_amount = 0;
        (this.state.items || []).forEach(item => {
            total_amount += Number(item.amount || 0);
        });

        this.props.form.setFieldsValue({ total_amount });

        this.calculateNetTotal();
    };

    getProductAmount = (items = this.state.items) => (items || [])
        .filter(item => item.type === 'Sản phẩm')
        .reduce((sum, item) => sum + Number(item.amount_to_pay !== undefined
            && item.amount_to_pay !== null ? item.amount_to_pay : Number(item.price || 0) * Number(item.quantity || 0)), 0);

    getServiceAmount = (items = this.state.items) => (items || [])
        .filter(item => item.type === 'Dịch vụ' && item.customer_service_id)
        .reduce((sum, item) => sum + Math.max(0, Number(item.amount) || 0), 0);

    onChangeFieldTotal = (field, value) => {
        this.props.form.setFieldsValue({ [field]: value });
        if (field === 'total_amount' || field === 'special_discount') {
            this.calculateNetTotal();
        }
    };

    calculateNetTotal = (overrides = {}) => {
        const productAmount = this.getProductAmount();
        const serviceAmount = this.getServiceAmount();
        const special_discount = Number(this.props.form.getFieldValue('special_discount') || 0);
        const balanceAmount = Number(overrides.balance_amount !== undefined
            ? overrides.balance_amount : this.props.form.getFieldValue('balance_amount')) || 0;
        const depositAmount = Number(overrides.deposit_amount !== undefined
            ? overrides.deposit_amount : this.props.form.getFieldValue('deposit_amount')) || 0;
        const serviceCashAmount = Math.max(0, serviceAmount - special_discount - balanceAmount - depositAmount);
        const net_total = Math.max(0, productAmount + serviceCashAmount);

        this.setState(prevState => ({
            special_discount,
            net_total,
            payment_breakdowns: prevState.payment_breakdowns.length === 1
                ? [{ ...prevState.payment_breakdowns[0], amount: net_total }]
                : prevState.payment_breakdowns,
        }));

        // set trực tiếp, không cần check !== undefined
        this.props.form.setFieldsValue({ net_total });
    };

    addPaymentBreakdown = () => {
        this.setState(state => ({
            payment_breakdowns: [...state.payment_breakdowns, {
                key: `payment-${Date.now()}-${Math.random()}`, payment_method: null, amount: 0,
                provider_name: 'VNĐ',
            }]
        }));
    };

    updatePaymentBreakdown = (key, field, value) => {
        this.setState(state => {
            const payment_breakdowns = state.payment_breakdowns.map(row => row.key === key ? { ...row, [field]: value } : row);
            const net_total = payment_breakdowns.reduce((sum, row) => sum + Number(row.amount || 0), 0);
            this.props.form.setFieldsValue({ net_total });
            return { payment_breakdowns, net_total };
        });
    };

    removePaymentBreakdown = key => {
        this.setState(state => {
            const payment_breakdowns = state.payment_breakdowns.filter(row => row.key !== key);
            const net_total = payment_breakdowns.reduce((sum, row) => sum + Number(row.amount || 0), 0);
            this.props.form.setFieldsValue({ net_total });
            return { payment_breakdowns, net_total };
        });
    };

    handleImageChange = (fileList) => {
        const images = (fileList || []).map((file) => ({
            manual_id: file.manual_id ?? file.uid ?? file.id ?? null,
            src: file.originalImage ?? file.src ?? file.url ?? null,
        })).filter(x => x.src);

        this.setState({ images });
    };

    render() {
        var {
            isLoading, submitting, items, customer_debt, customer_balance, deposit_data, net_total, payment_breakdowns,
        } = this.state;
        var { visible, currentReceipt } = this.props;
        const { getFieldDecorator } = this.props.form;
        const isRefundMetadataOnly = currentReceipt?.refund_summary?.status === 'PARTIAL';
        const availableBalance = currentReceipt
            ? Number(currentReceipt.customer_unallocated_balance || 0)
            : Number(customer_balance || 0);
        const activeDeposits = currentReceipt ? [] : (deposit_data.active_deposits || [])
            .filter(deposit => Number(deposit.current_balance || 0) > 0);
        const storedDepositIds = this.props.form.getFieldValue('deposit_ids');
        const selectedDepositIds = Array.isArray(storedDepositIds)
            ? storedDepositIds
            : activeDeposits.length === 1
                ? [Number(activeDeposits[0].id)]
                : [];
        const selectedDepositBalance = getSelectedDepositBalance(selectedDepositIds, activeDeposits);
        const activeDepositBalance = activeDeposits.reduce(
            (sum, deposit) => sum + Number(deposit.current_balance || 0),
            0
        );
        const displayedDepositBalance = selectedDepositIds.length > 0
            ? selectedDepositBalance
            : activeDepositBalance;
        const depositAmount = Number(this.props.form.getFieldValue('deposit_amount') || 0);
        const balanceAmountForSummary = Number(this.props.form.getFieldValue('balance_amount') || 0);
        const serviceAmount = this.getServiceAmount(items);
        const balanceLimit = Math.min(availableBalance, Math.max(0,
            serviceAmount - Number(this.state.special_discount || 0) - depositAmount));
        const depositLimit = getReceiptDepositLimit(items, selectedDepositBalance, 0,
            balanceAmountForSummary, this.state.special_discount);

        var columns = [
            {
                title: "STT",
                dataIndex: "stt",
                width: "40px",
                align: 'center',
                render: (text, record, index) => index + 1
            },
            {
                title: "Sản phẩm/Dịch vụ",
                dataIndex: "name"
            },
            {
                title: "Loại",
                dataIndex: "type",
                width: '100px'
            },
            {
                title: "Số lượng",
                dataIndex: "quantity",
                width: '80px',
                render: (text, record) => {
                    return (
                        <InputNumber
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            value={text}
                            name="quantity"
                            min={1}
                            className='w-100 fix-height-input'
                            disabled={record.type === 'Dịch vụ' || Boolean(record.product_sale_id)}
                            onChange={(value) => {
                                const validValue = value == null || value <= 0 ? 1 : value;
                                this.onChangeItem('quantity', validValue, record.key);
                            }}
                        />
                    )
                }
            },
            {
                title: "Giá bán",
                dataIndex: "price",
                width: "120px",
                render: (text, record) => {
                    return (
                        <InputNumber
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            value={text}
                            name="price"
                            step={50000}
                            min={0}
                            disabled={record.type === 'Dịch vụ' || Boolean(record.product_sale_id)}
                            className='w-100 fix-height-input'
                            onChange={(value) => this.onChangeItem('price', value, record.key)}
                        />
                    )
                }
            },
            {
                title: "Thành tiền",
                dataIndex: "line_total",
                width: "120px",
                render: (_, record) => <>{renderMoney(Number(record.price || 0) * Number(record.quantity || 0))}</>
            },
            {
                title: fieldLabel(
                    'Số tiền cần thu',
                    'Số tiền khách còn cần thanh toán cho dịch vụ này tại thời điểm lập phiếu.'
                ),
                dataIndex: "outstanding_amount",
                width: "140px",
                render: (_, record) => {
                    let amountToCollect = record.outstanding_amount;
                    if (amountToCollect === undefined || amountToCollect === null) {
                        amountToCollect = record.amount_to_pay;
                    }
                    if (amountToCollect === undefined || amountToCollect === null) {
                        amountToCollect = record.amount || 0;
                    }
                    return <>{renderMoney(Number(amountToCollect))}</>;
                }
            },
            {
                title: fieldLabel(
                    'Đã phân bổ',
                    'Số tiền đã được ghi nhận và phân bổ vào dịch vụ này từ các lần thanh toán trước.'
                ),
                dataIndex: "allocated_amount",
                width: "120px",
                render: (value, record) => record.type === 'Dịch vụ'
                    ? <>{renderMoney(Number(value || 0))}</>
                    : <>-</>
            },
            {
                title: "#",
                align: "center",
                width: '30px',
                render: (text, record) => {
                    return (
                        <Popconfirm
                            title="Xóa bản ghi này?"
                            placement='left'
                            onConfirm={() => this.onDelete(record.key)}
                        >
                            <DeleteOutlined className='item-action-btn-remove' />
                        </Popconfirm>
                    )
                }
            }
        ];

        var total_amount = 0;
        items.forEach(item => {
            total_amount += Number(item.amount || 0);
        });
        const availableCustomerServices = getAvailableCustomerServices(
            this.state.available_customer_services,
            items
        );

        return (
            <Drawer
                visible={visible}
                title={isRefundMetadataOnly ? 'Cập nhật ghi chú / ảnh phiếu thu' : currentReceipt ? 'Sửa phiếu thu' : `Tạo phiếu thu`}
                onOk={(e) => this.submit(e)}
                onClose={() => this.onClose()}
                maskClosable={false}
                width={isMobile ? '100%' : '65%'}
                footer={
                    <div style={{ textAlign: 'right', }}>
                        <Space>
                            <Button size='large' key="back" onClick={() => this.onClose()}>
                                Huỷ
                            </Button>
                            <Button
                                loading={submitting}
                                key="submit"
                                type="primary"
                                size="large"
                                onClick={(e) => this.submit(e)}
                            >
                                {isRefundMetadataOnly ? 'Lưu ghi chú / ảnh' : currentReceipt ? 'Cập nhật' : "Tạo phiếu"}
                            </Button>
                        </Space>
                    </div>
                }
            >
                {visible ?
                    <Form layout='vertical' className={`receipt-form${isRefundMetadataOnly ? ' receipt-form--refund-locked' : ''}`}>
                        {isRefundMetadataOnly && (
                            <Alert
                                className="mb-3"
                                type="warning"
                                message="Phiếu đã hoàn một phần"
                                description="Chỉ ghi chú và ảnh chứng từ còn được cập nhật. Toàn bộ trường tài chính, dịch vụ, khách hàng, ngày thu, cơ sở và hình thức thu đã khóa; thay đổi tài chính phải thực hiện qua Hoàn tiền / Điều chỉnh."
                            />
                        )}
                        <div className="receipt-form__hidden-fields">
                            {getFieldDecorator('balance_reason', {
                                initialValue: currentReceipt
                                    ? 'Sử dụng số dư khi cập nhật phiếu thu.'
                                    : 'Sử dụng số dư khi tạo phiếu thu.',
                            })(<Input type="hidden" />)}
                            {getFieldDecorator('deposit_reason', {
                                initialValue: currentReceipt
                                    ? 'Sử dụng tiền cọc khi cập nhật phiếu thu.'
                                    : 'Sử dụng tiền cọc khi tạo phiếu thu.',
                            })(<Input type="hidden" />)}
                        </div>
                        <Spin tip="Loading..." spinning={isLoading}>
                            <div className="receipt-form__sections">
                                <fieldset
                                    disabled={isRefundMetadataOnly}
                                    style={{
                                        border: 0,
                                        margin: 0,
                                        padding: 0,
                                        minWidth: 0,
                                        opacity: isRefundMetadataOnly ? 0.62 : 1,
                                        pointerEvents: isRefundMetadataOnly ? 'none' : 'auto',
                                    }}
                                >
                                    <Row gutter={16}>
                                        {
                                            currentReceipt ? (
                                                <Col xs={24}>
                                                    <div className="receipt-form__customer-summary">
                                                        <UserOutlined />
                                                        <span>Khách hàng: <strong className='color-primary'>
                                                            {currentReceipt?.customer?.full_name + ' - ' + currentReceipt?.customer?.code}
                                                        </strong></span>
                                                    </div>
                                                </Col>
                                            ) : (
                                                <Col xs={24} md={24}>
                                                    <Form.Item label={fieldLabel('Khách hàng', 'Chọn người thực tế nộp tiền. Hệ thống dùng khách hàng này để tải đúng công nợ dịch vụ và số dư chưa phân bổ.')}>
                                                        {getFieldDecorator("customer_id", {
                                                            rules: [
                                                                {
                                                                    required: true,
                                                                    message: "Vui lòng chọn!",
                                                                },
                                                            ],
                                                            initialValue: currentReceipt ? currentReceipt?.customer_id : null
                                                        })(
                                                            <CustomerSelect
                                                                onChange={(id, label) => this.onChangeCustomer(id, label)}
                                                            />
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                            )
                                        }
                                    </Row>
                                    {currentReceipt && (
                                        <Row gutter={16}>
                                            <Col xs={24}>
                                                <Form.Item label={fieldLabel('Thêm dịch vụ', 'Chỉ hiển thị dịch vụ còn công nợ của đúng khách hàng và chưa có trong phiếu thu.')}>
                                                    <Space className="w-100" align="start">
                                                        <Select
                                                            mode="multiple"
                                                            showSearch
                                                            allowClear
                                                            loading={this.state.service_options_loading}
                                                            value={this.state.selected_customer_service_ids}
                                                            placeholder="Chọn một hoặc nhiều dịch vụ"
                                                            optionFilterProp="children"
                                                            style={{ minWidth: isMobile ? '65vw' : '45vw' }}
                                                            onChange={selected_customer_service_ids => this.setState({ selected_customer_service_ids })}
                                                        >
                                                            {availableCustomerServices.map(service => (
                                                                <Select.Option key={service.id} value={Number(service.id)}>
                                                                    {(service.service?.name || service.service_name || `Dịch vụ #${service.id}`)} — còn {renderMoney(Number(service.outstanding_amount || 0))}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                        <Button
                                                            type="dashed"
                                                            disabled={!this.state.selected_customer_service_ids.length}
                                                            onClick={this.addSelectedCustomerServices}
                                                        >
                                                            Thêm
                                                        </Button>
                                                    </Space>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    )}
                                    <Row gutter={16}>
                                        <Col xs={24}>
                                            <Form.Item label={fieldLabel('Sản phẩm', 'Chọn sản phẩm xuất kèm phiếu nếu có; số lượng và giá bán được đưa vào nội dung thu nhưng không thay đổi nghĩa vụ của dịch vụ.')}>
                                                <ProductSelect
                                                    defaultText="Chọn sản phẩm"
                                                    optionValue="id"
                                                    optionLabel="name"
                                                    additionalLabel1="quantity"
                                                    additionalLabel2="expired_at"
                                                    additionalLabel3="warehouse_name"
                                                    onChange={(id, label) => this.onChangeProduct(id, label)}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col xs={24}>
                                            <Table
                                                className='receipt-form__table desabled-input-table'
                                                size='small'
                                                rowKey='key'
                                                locale={{ emptyText: "Chưa có dữ liệu" }}
                                                tableLayout="auto"
                                                columns={columns}
                                                dataSource={items || []}
                                                pagination={false}
                                            />
                                        </Col>
                                    </Row>
                                    <div className="receipt-form__financial-fields">
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Tổng cần thu theo nội dung', 'Tổng các cột Số tiền cần thu của nội dung đang chọn; hệ thống tự tính và không yêu cầu nhập lại.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    {getFieldDecorator('total_amount', {
                                                        rules: [
                                                            {
                                                                required: true,
                                                                message: 'Vui lòng điền!',
                                                            },
                                                        ],
                                                        initialValue: currentReceipt ? currentReceipt.total_amount : total_amount
                                                    })(
                                                        <InputNumber
                                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                            className="w-100"
                                                            step={50000}
                                                            min={0}
                                                            disabled
                                                            onChange={(value) => this.onChangeFieldTotal('total_amount', value)}
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Công nợ hiện tại', 'Tổng nghĩa vụ dịch vụ còn lại của khách trước giao dịch này; tiền thu vượt không làm công nợ âm mà chuyển thành số dư chưa phân bổ.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    {getFieldDecorator('outstanding_debt', {
                                                        rules: [
                                                            {
                                                                required: false,
                                                                message: "Vui lòng điền!",
                                                            },
                                                        ],
                                                        initialValue: Math.max(0, Number(currentReceipt ? currentReceipt.remaining_debt ?? 0 : customer_debt) || 0)
                                                    })(
                                                        <InputNumber
                                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                            className="w-100"
                                                            step={50000}
                                                            min={0}
                                                            disabled
                                                            placeholder='Đơn vị vnđ'
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Ưu đãi đặc biệt', 'Khoản giảm nghĩa vụ được phân bổ cho các dịch vụ trong phiếu; nhập đúng căn cứ vì thay đổi này ảnh hưởng công nợ và doanh thu đủ điều kiện.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    {getFieldDecorator('special_discount', {
                                                        rules: [
                                                            {
                                                                required: true,
                                                                message: "Vui lòng điền!",
                                                            },
                                                        ],
                                                        initialValue: currentReceipt ? currentReceipt.special_discount : 0
                                                    })(
                                                        <InputNumber
                                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                            className="w-100"
                                                            step={50000}
                                                            min={0}
                                                            placeholder='Đơn vị vnđ'
                                                            onChange={(value) => this.onChangeFieldTotal('special_discount', value)}
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Khách thực nộp', 'Tiền thực nộp được tự động phân bổ đều cho các sản phẩm và dịch vụ còn phải thanh toán, không vượt quá công nợ của từng khoản; phần tiền vượt tổng nghĩa vụ thanh toán mới được ghi nhận vào số dư khách hàng.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    {getFieldDecorator('net_total', { initialValue: currentReceipt ? currentReceipt.amount_paid : net_total })(
                                                        <InputNumber disabled formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} className="w-100" />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>

                                    <ReceiptSection
                                        icon={<CreditCardOutlined />}
                                        title="Hình thức thanh toán"
                                        description="Tách chính xác số tiền khách thực nộp theo từng phương thức."
                                        financial
                                        extra={<Button size="small" icon={<PlusOutlined />} onClick={this.addPaymentBreakdown}>Thêm hình thức</Button>}
                                    >
                                        {payment_breakdowns.map((row, index) => {
                                            const usedMethods = payment_breakdowns.filter(item => item.key !== row.key).map(item => item.payment_method);
                                            return <Row className="receipt-payment-row" gutter={8} key={row.key} align="middle">
                                                <Col xs={20} md={11}><Form.Item label={index === 0 ? 'Hình thức thanh toán' : ''} required>
                                                    <Select value={row.payment_method} placeholder="Chọn hình thức" onChange={value => this.updatePaymentBreakdown(row.key, 'payment_method', value)}
                                                        options={(paymentMethodOptions || []).filter(item => !usedMethods.includes(item.value)).map(item => ({ value: item.value, label: item.label }))} />
                                                </Form.Item></Col>
                                                <Col xs={20} md={11}><Form.Item label={index === 0 ? 'Số tiền' : ''} required>
                                                    <InputNumber min={0} precision={0} step={50000} value={row.amount} className="w-100"
                                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                        onChange={value => this.updatePaymentBreakdown(row.key, 'amount', Number(value || 0))} />
                                                </Form.Item></Col>
                                                <Col className="receipt-payment-row__remove" xs={4} md={2}><Button danger type="text" icon={<DeleteOutlined />} disabled={payment_breakdowns.length === 1}
                                                    onClick={() => this.removePaymentBreakdown(row.key)} /></Col>
                                            </Row>;
                                        })}
                                    </ReceiptSection>
                                    {(availableBalance > 0 || activeDeposits.length > 0) && (
                                        <Row className="receipt-payment-sources" gutter={[16, 16]} align="stretch">
                                            {availableBalance > 0 && (
                                                <Col className="receipt-payment-sources__col" xs={24} md={12}>
                                                    <PaymentSourceBox
                                                        icon={<WalletOutlined />}
                                                        title="Số dư khách hàng"
                                                        available={availableBalance}
                                                        remaining={Math.max(0, availableBalance - balanceAmountForSummary)}
                                                        target="Tất cả dịch vụ còn phải thanh toán"
                                                        tone="balance"
                                                    >
                                                        {getFieldDecorator('balance_customer_service_id', {
                                                            initialValue: 'ALL',
                                                        })(<Input type="hidden" />)}

                                                        <Form.Item className="receipt-payment-source__form-item" label="Số tiền sử dụng">
                                                            <div className="receipt-payment-source__amount-row">
                                                                {getFieldDecorator('balance_amount', {
                                                                    initialValue: 0,
                                                                })(
                                                                    <InputNumber
                                                                        min={0}
                                                                        max={balanceLimit}
                                                                        precision={0}
                                                                        step={50000}
                                                                        className="receipt-payment-source__amount-input"
                                                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                        onChange={value => this.calculateNetTotal({ balance_amount: Number(value || 0) })}
                                                                    />
                                                                )}
                                                                <Button
                                                                    htmlType="button"
                                                                    onClick={() => {
                                                                        this.props.form.setFieldsValue({ balance_amount: balanceLimit });
                                                                        this.calculateNetTotal({ balance_amount: balanceLimit });
                                                                    }}
                                                                >
                                                                    Dùng hết
                                                                </Button>
                                                            </div>
                                                        </Form.Item>
                                                    </PaymentSourceBox>
                                                </Col>
                                            )}

                                            {activeDeposits.length > 0 && (
                                                <Col className="receipt-payment-sources__col" xs={24} md={12}>
                                                    <PaymentSourceBox
                                                        icon={<BankOutlined />}
                                                        title="Tiền cọc"
                                                        available={displayedDepositBalance}
                                                        remaining={Math.max(0, displayedDepositBalance - depositAmount)}
                                                        target="Các dịch vụ còn phải thanh toán trong phiếu"
                                                        tone="deposit"
                                                    >
                                                        {activeDeposits.length > 1 ? (
                                                            <Form.Item className="receipt-payment-source__form-item" label="Khoản cọc">
                                                                {getFieldDecorator('deposit_ids', {
                                                                    initialValue: selectedDepositIds,
                                                                    rules: [{
                                                                        validator: (_, value, callback) => {
                                                                            callback(depositAmount > 0 && (!value || value.length === 0)
                                                                                ? 'Vui lòng chọn ít nhất một khoản cọc.'
                                                                                : undefined);
                                                                        }
                                                                    }]
                                                                })(
                                                                    <Select
                                                                        mode="multiple"
                                                                        showSearch
                                                                        optionFilterProp="label"
                                                                        placeholder="Chọn khoản cọc"
                                                                        style={{ width: '100%' }}
                                                                        options={activeDeposits.map(deposit => ({
                                                                            value: Number(deposit.id),
                                                                            label: `${deposit.receipt_code || `Cọc #${deposit.id}`} - còn ${renderMoney(deposit.current_balance)}`,
                                                                        }))}
                                                                        onChange={() => {
                                                                            this.props.form.setFieldsValue({ deposit_amount: 0 });
                                                                            this.calculateNetTotal({ deposit_amount: 0 });
                                                                        }}
                                                                    />
                                                                )}
                                                            </Form.Item>
                                                        ) : (
                                                            getFieldDecorator('deposit_ids', {
                                                                initialValue: selectedDepositIds,
                                                            })(<Input type="hidden" />)
                                                        )}

                                                        <Form.Item className="receipt-payment-source__form-item" label="Số tiền sử dụng">
                                                            <div className="receipt-payment-source__amount-row">
                                                                {getFieldDecorator('deposit_amount', {
                                                                    initialValue: 0,
                                                                    rules: [{
                                                                        validator: (_, value, callback) => {
                                                                            const amount = Number(value || 0);
                                                                            callback(amount > depositLimit
                                                                                ? 'Số tiền vượt quá khoản cọc có thể sử dụng cho phiếu này.'
                                                                                : undefined);
                                                                        }
                                                                    }]
                                                                })(
                                                                    <InputNumber
                                                                        min={0}
                                                                        max={depositLimit}
                                                                        precision={0}
                                                                        step={50000}
                                                                        className="receipt-payment-source__amount-input"
                                                                        disabled={selectedDepositIds.length === 0}
                                                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                        onChange={value => this.calculateNetTotal({ deposit_amount: Number(value || 0) })}
                                                                    />
                                                                )}
                                                                <Button
                                                                    htmlType="button"
                                                                    disabled={selectedDepositIds.length === 0 || depositLimit <= 0}
                                                                    onClick={() => {
                                                                        this.props.form.setFieldsValue({ deposit_amount: depositLimit });
                                                                        this.calculateNetTotal({ deposit_amount: depositLimit });
                                                                    }}
                                                                >
                                                                    Dùng hết
                                                                </Button>
                                                            </div>
                                                        </Form.Item>
                                                    </PaymentSourceBox>
                                                </Col>
                                            )}
                                        </Row>
                                    )}
                                </fieldset>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label={fieldLabel('Cơ sở thu tiền', 'Chọn đúng nơi thực tế nhận tiền; giá trị này được lưu cố định cho lịch sử và đối soát.')}>
                                            {getFieldDecorator('branch_id', {
                                                rules: [{ required: true, message: 'Vui lòng chọn cơ sở thực tế thu tiền.' }],
                                                initialValue: currentReceipt ? currentReceipt.branch_id : null
                                            })(<Select disabled={isRefundMetadataOnly} showSearch optionFilterProp="label" options={(this.props.config.branches || []).map(branch => ({ value: branch.id, label: branch.name }))} />)}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item label={fieldLabel('Ngày thu', 'Ngày thực tế nhận tiền, dùng làm thời điểm phân bổ và xác định chính sách hoa hồng có hiệu lực.')}>
                                            {getFieldDecorator('payment_at', {
                                                rules: [
                                                    {
                                                        required: true,
                                                        message: 'Vui lòng chọn!',
                                                    },
                                                ],
                                                initialValue: currentReceipt ? isValidDateForm(currentReceipt.payment_at, false) : moment()
                                            })(
                                                <DatePicker
                                                    className='w-100'
                                                    format="DD/MM/YYYY"
                                                    disabled={isRefundMetadataOnly}
                                                />
                                            )}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item label={fieldLabel('Ghi chú', 'Bổ sung nội dung thu hoặc căn cứ ưu đãi để người dùng khác có thể kiểm tra giao dịch.')}>
                                            {getFieldDecorator('note', {
                                                rules: [
                                                    {
                                                        required: false,
                                                        message: "Vui lòng điền!",
                                                    },
                                                ],
                                                initialValue: currentReceipt ? _newLine(currentReceipt.note) : null
                                            })(
                                                <Input.TextArea rows={3} />
                                            )}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item label={fieldLabel(<><PaperClipOutlined /> Ảnh phiếu</>, 'Đính kèm ảnh chứng từ thu để phục vụ tra cứu và đối chiếu; không tải lên thông tin không liên quan.')} className='flex-upload'>
                                            <ImageSelector
                                                key={currentReceipt?.id || 'new'}
                                                onChangeData={(fileList, changeDetails) => this.handleImageChange(fileList, changeDetails)} // Gắn hàm xử lý thay đổi dữ liệu
                                                multiple={true} // Cho phép chọn nhiều ảnh
                                                maxCount={50} // Giới hạn tối đa 50 ảnh
                                                uploadText="Chọn ảnh"
                                                defaultFileList={currentReceipt ? currentReceipt.images : []}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        </Spin>
                    </Form>
                    : null
                }
            </Drawer>
        )
    }
}

function mapStateToProps(state) {
    return {
        config: state.config,
        users: state.config.users,
        authUser: state.auth.authUser,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        createReceipt: (data) => dispatch(createReceipt(data)),
        updateReceipt: (id, data) => dispatch(updateReceipt(id, data)),
        previewReceiptMutation: (id, data) => dispatch(previewReceiptMutation(id, data)),
        fetchProduct: (id) => dispatch(fetchProduct(id)),
        fetchCustomer: (id, filter) => dispatch(fetchCustomer(id, filter)),
        getCustomerDepositSummary: (id) => dispatch(getCustomerDepositSummary(id))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Form.create({ name: 'ReceiptForm' })(ReceiptForm));
